import { useState, useEffect } from "react";
import { C } from "./ui/theme";

// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — this file used to define its own local Fluent/Azure-blue palette here
// (`const T = {...}`, accent:"#0078D4"), completely separate from
// ui/theme.js's Apple palette `C`. Every `C.xxx` reference below has been
// renamed to `C.xxx` (a pure token-name swap, zero value remapping) and the
// local palette object removed. One incidental fix: the old `T` object had
// no `cyan` key, so the "Sent" stat's color (`C.cyan`) was silently
// `undefined` — `C.cyan` actually resolves, so that stat now renders in its
// intended color instead of falling back to plain text-black.

const ALL_PERMISSIONS = [
  { key:"contacts",   label:"Contacts",   icon:"" },
  { key:"campaigns",  label:"Campaigns",  icon:"" },
  { key:"sequences",  label:"Sequences",  icon:"" },
  { key:"templates",  label:"Templates",  icon:"" },
  { key:"dashboard",  label:"Dashboard",  icon:"" },
  { key:"reports",    label:"Reports",    icon:"" },
];

const ROLES = ["Admin","SDR Manager","SDR","Marketing","Support"];

function Avatar({init,size=36,color=C.accent}){
  return(
    <div style={{width:size,height:size,borderRadius:"50%",background:color+"1A",border:"1px solid "+color+"40",display:"flex",alignItems:"center",justifyContent:"center",fontSize:Math.round(size*.35),fontWeight:500,color,flexShrink:0,fontFamily:"monospace"}}>
      {init}
    </div>
  );
}

function Badge({children,bg,tc,sm}){
  return <span style={{background:bg,color:tc,fontSize:sm?10:11,padding:sm?"2px 7px":"3px 9px",borderRadius:99,fontWeight:500,whiteSpace:"nowrap"}}>{children}</span>;
}

function Toggle({on,onChange}){
  return(
    <div onClick={()=>onChange(!on)} style={{width:36,height:20,borderRadius:10,background:on?C.green:C.border,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
      <div style={{width:14,height:14,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:on?19:3,transition:"left .2s",boxShadow:"0 1px 3px #0006"}}/>
    </div>
  );
}

export default function TeamPage({addToast}){
  const toast=addToast||function(){};
  const [team,setTeam]=useState([]);
  const [loading,setLoading]=useState(false);
  const [showAdd,setShowAdd]=useState(false);
  const [editingMember,setEditingMember]=useState(null);
  const [showPermissions,setShowPermissions]=useState(null);
  const [newMember,setNewMember]=useState({name:"",role:"SDR",email:"",phone:""});
  const [saving,setSaving]=useState(false);
  const [search,setSearch]=useState("");

  // Real, login-capable teammate invites — distinct from the lightweight
  // name/email "team member" tag above (which never creates a login). See
  // server.js's /team/invite* routes and db.js's TeamInvite model.
  const [invites,setInvites]=useState([]);
  const [showInvite,setShowInvite]=useState(false);
  const [inviteEmail,setInviteEmail]=useState("");
  const [inviteRole,setInviteRole]=useState("user");
  const [inviting,setInviting]=useState(false);
  const [revokingId,setRevokingId]=useState(null);
  // Placeholder per-seat price — pulled from /billing/status (server.js's
  // SEAT_PRICE_USD) rather than hardcoded, so the copy stays correct if
  // that env var is ever changed. Falls back to 20 until it loads.
  const [seatPriceUsd,setSeatPriceUsd]=useState(20);

  useEffect(()=>{fetchTeam();fetchInvites();fetchSeatPrice();},[]);

  const authHeaders=()=>({Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")});

  const fetchSeatPrice=async()=>{
    try{
      const r=await fetch("/api/billing/status",{headers:authHeaders()});
      if(r.ok){const d=await r.json();if(d.seatPriceUsd)setSeatPriceUsd(d.seatPriceUsd);}
    }catch(e){}
  };

  const fetchInvites=async()=>{
    try{
      const r=await fetch("/api/team/invites",{headers:authHeaders()});
      if(r.ok)setInvites(await r.json());
    }catch(e){}
  };

  const sendInvite=async()=>{
    const email=inviteEmail.trim();
    if(!email){toast("Enter an email address","error");return;}
    setInviting(true);
    try{
      const r=await fetch("/api/team/invite",{method:"POST",headers:{"Content-Type":"application/json",...authHeaders()},body:JSON.stringify({email,role:inviteRole})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error||"Couldn't send the invite.");
      toast(d.emailSent?"Invite sent to "+email:"Invite created, but the email couldn't be sent — share the workspace link with them directly.",d.emailSent?"success":"info");
      setInviteEmail("");
      fetchInvites();
    }catch(e){toast(e.message,"error");}
    setInviting(false);
  };

  const revokeInvite=async(id)=>{
    setRevokingId(id);
    try{
      const r=await fetch("/api/team/invites/"+id,{method:"DELETE",headers:authHeaders()});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setInvites(p=>p.filter(i=>i.id!==id));
      toast("Invite revoked","info");
    }catch(e){toast(e.message,"error");}
    setRevokingId(null);
  };
  const fetchTeam=async()=>{
    setLoading(true);
    try{
      const r=await fetch("/api/team",{headers:authHeaders()});
      const d=await r.json();
      // deduplicate by email
      const seen=new Set();
      const unique=d.filter(m=>{
        if(seen.has(m.email))return false;
        seen.add(m.email);return true;
      });
      setTeam(unique);
    }catch(e){}
    setLoading(false);
  };

  const addMember=async()=>{
    if(!newMember.name||!newMember.email){toast("Name and email required","error");return;}
    setSaving(true);
    try{
      const r=await fetch("/api/team",{method:"POST",headers:{"Content-Type":"application/json",...authHeaders()},body:JSON.stringify(newMember)});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setTeam(p=>[...p,d]);
      setShowAdd(false);
      setNewMember({name:"",role:"SDR",email:"",phone:""});
      toast("Team member added","success");
    }catch(e){toast(e.message,"error");}
    setSaving(false);
  };

  const updateMember=async(id,updates)=>{
    try{
      const r=await fetch("/api/team/"+id,{method:"PUT",headers:{"Content-Type":"application/json",...authHeaders()},body:JSON.stringify(updates)});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setTeam(p=>p.map(m=>m.id===id?d:m));
      setEditingMember(null);
      toast("Updated","success");
    }catch(e){toast(e.message,"error");}
  };

  const deleteMember=async(id)=>{
    try{
      const r=await fetch("/api/team/"+id,{method:"DELETE",headers:authHeaders()});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      setTeam(p=>p.filter(m=>m.id!==id));
      toast("Team member removed","info");
    }catch(e){toast(e.message,"error");}
  };

  const toggleActive=async(id)=>{
    try{
      const r=await fetch("/api/team/"+id+"/toggle",{method:"PATCH",headers:authHeaders()});
      const d=await r.json();
      setTeam(p=>p.map(m=>m.id===id?d:m));
      toast(d.active?"User activated":"User deactivated","info");
    }catch(e){toast(e.message,"error");}
  };

  const updatePermissions=async(id,permissions)=>{
    try{
      const r=await fetch("/api/team/"+id+"/permissions",{method:"PUT",headers:{"Content-Type":"application/json",...authHeaders()},body:JSON.stringify({permissions})});
      const d=await r.json();
      setTeam(p=>p.map(m=>m.id===id?d:m));
      toast("Permissions updated","success");
    }catch(e){toast(e.message,"error");}
  };

  const roleColor=(role)=>role==="Admin"?C.purple:role==="SDR Manager"?C.green:C.accent;

  const filtered=team.filter(m=>
    (m.name||"").toLowerCase().includes(search.toLowerCase())||
    (m.email||"").toLowerCase().includes(search.toLowerCase())||
    (m.role||"").toLowerCase().includes(search.toLowerCase())
  );

  return(
    <div style={{fontFamily:"'Geist',sans-serif",animation:"fadeUp .25s ease both"}}>
      <style>{"@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}"}</style>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:22,flexWrap:"wrap",gap:10}}>
        <div>
          {/* Plain professional title — inherits the page's own 'Geist'
              sans-serif; no italic script face, no rainbow gradient fill. */}
          <p style={{fontSize:21,fontWeight:650,letterSpacing:"-.3px",lineHeight:1.25,color:C.text}}>Team Workspace</p>
          <p style={{fontSize:13,color:C.textSub,marginTop:3}}>{team.length} members  {team.filter(m=>m.active).length} active</p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setShowInvite(!showInvite)} style={{background:C.accentMuted,color:C.accent,border:"1px solid "+C.accent+"44",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:500,cursor:"pointer"}}>
            + Invite a teammate
          </button>
          <button onClick={()=>setShowAdd(!showAdd)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:500,cursor:"pointer"}}>
            + Add team member
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
        {[
          {label:"Total Members",value:team.length,color:C.accent},
          {label:"Active",value:team.filter(m=>m.active).length,color:C.green},
          {label:"Inactive",value:team.filter(m=>!m.active).length,color:C.amber},
          {label:"Admins",value:team.filter(m=>m.role==="Admin").length,color:C.purple},
        ].map(s=>(
          <div key={s.label} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:10,padding:"14px 16px"}}>
            <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>{s.label}</p>
            <p style={{fontSize:24,fontWeight:700,color:s.color,fontFamily:"monospace",lineHeight:1}}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Invite a teammate — creates a real, login-capable account under this
          same workspace, unlike the lightweight "Add team member" tag below.
          Card-free for now, with a placeholder per-seat price (see
          server.js's SEAT_PRICE_USD) — nothing is charged automatically. */}
      {showInvite&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.accent+"55",borderRadius:12,padding:20,marginBottom:16}}>
          <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>Invite a teammate to your workspace</p>
          <p style={{fontSize:12,color:C.textSub,marginBottom:14,lineHeight:1.5}}>
            They'll get a real login and share this same workspace — contacts, campaigns, and sequences included.
            Each seat is <strong style={{color:C.text}}>${seatPriceUsd}/month</strong> (placeholder pricing — nothing is charged during this trial).
          </p>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"2fr 1fr auto",gap:10}}>
            <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="teammate@company.com" type="email"
              onKeyDown={e=>{if(e.key==="Enter")sendInvite();}}
              style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
            <select value={inviteRole} onChange={e=>setInviteRole(e.target.value)}
              style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}>
              <option value="user">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button onClick={sendInvite} disabled={inviting} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"}}>
              {inviting?"Sending...":"Send invite"}
            </button>
          </div>

          {invites.length>0&&(
            <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid "+C.border}}>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:10}}>Pending invites</p>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {invites.map(inv=>(
                  <div key={inv.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",background:C.bgHover,borderRadius:8,border:"1px solid "+C.border}}>
                    <div style={{flex:1,minWidth:160}}>
                      <span style={{fontSize:13,color:C.text,fontWeight:500}}>{inv.email}</span>
                      <span style={{fontSize:11,color:C.textSub,marginLeft:8}}>{inv.role==="admin"?"Admin":"Member"}  invited by {inv.invitedByName||"you"}</span>
                    </div>
                    <Badge bg={C.amberMuted} tc={C.amber} sm>Pending</Badge>
                    <button onClick={()=>revokeInvite(inv.id)} disabled={revokingId===inv.id} style={{background:C.redMuted,color:C.red,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                      {revokingId===inv.id?"Revoking...":"Revoke"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add member form */}
      {showAdd&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.accent+"55",borderRadius:12,padding:20,marginBottom:16}}>
          <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:14}}>Add New Team Member</p>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:12}}>
            <div>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>Full Name *</p>
              <input value={newMember.name} onChange={e=>setNewMember(p=>({...p,name:e.target.value}))} placeholder="John Smith"
                style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
            </div>
            <div>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>Email *</p>
              <input value={newMember.email} onChange={e=>setNewMember(p=>({...p,email:e.target.value}))} placeholder="john@company.com"
                style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
            </div>
            <div>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>Role</p>
              <select value={newMember.role} onChange={e=>setNewMember(p=>({...p,role:e.target.value}))}
                style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}>
                {ROLES.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>Phone</p>
              <input value={newMember.phone||""} onChange={e=>setNewMember(p=>({...p,phone:e.target.value}))} placeholder="+1 234 567 8900"
                style={{width:"100%",background:C.bgHover,border:"1px solid "+C.border,borderRadius:7,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={addMember} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,fontWeight:500,cursor:"pointer"}}>
              {saving?"Adding...":"Add member"}
            </button>
            <button onClick={()=>setShowAdd(false)} style={{background:"transparent",color:C.textSub,border:"1px solid "+C.border,borderRadius:8,padding:"8px 16px",fontSize:13,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{marginBottom:12}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search team members..."
          style={{width:"100%",maxWidth:320,background:C.bgCard,border:"1px solid "+C.border,borderRadius:8,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
      </div>

      {/* Team list */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {filtered.map(m=>{
          const rc=roleColor(m.role);
          const isEditing=editingMember?.id===m.id;
          const showPerms=showPermissions===m.id;
          return(
            <div key={m.id} style={{background:C.bgCard,border:"1px solid "+(m.active?C.border:C.border+"88"),borderRadius:12,overflow:"hidden",opacity:m.active?1:.7}}>

              {/* Member row */}
              <div style={{padding:"14px 20px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                <Avatar init={m.init||(m.name||"?").slice(0,2).toUpperCase()} size={40} color={rc}/>
                <div style={{flex:1,minWidth:200}}>
                  {isEditing?(
                    <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                      <input defaultValue={m.name} id={"name-"+m.id}
                        style={{background:C.bgHover,border:"1px solid "+C.border,borderRadius:6,padding:"5px 8px",color:C.text,fontSize:13,outline:"none"}}/>
                      <input defaultValue={m.email} id={"email-"+m.id}
                        style={{background:C.bgHover,border:"1px solid "+C.border,borderRadius:6,padding:"5px 8px",color:C.text,fontSize:13,outline:"none"}}/>
                      <select defaultValue={m.role} id={"role-"+m.id}
                        style={{background:C.bgHover,border:"1px solid "+C.border,borderRadius:6,padding:"5px 8px",color:C.text,fontSize:13,outline:"none"}}>
                        {ROLES.map(r=><option key={r}>{r}</option>)}
                      </select>
                    </div>
                  ):(
                    <>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                        <span style={{fontSize:14,fontWeight:600,color:C.text}}>{m.name}</span>
                        <Badge bg={rc+"1A"} tc={rc} sm>{m.role}</Badge>
                        {!m.active&&<Badge bg={C.amberMuted} tc={C.amber} sm>Inactive</Badge>}
                      </div>
                      <p style={{fontSize:12,color:C.textSub}}>{m.email}{m.phone?"  "+m.phone:""}</p>
                    </>
                  )}
                </div>

                {/* Stats */}
                {!isEditing&&(
                  <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                    {[["Leads",m.leads||0,C.accent],["Meetings",m.meetings||0,C.purple],["Sent",m.sent||0,C.cyan],["Replied",m.replied||0,C.green]].map(([l,v,c])=>(
                      <div key={l} style={{textAlign:"center"}}>
                        <p style={{fontSize:16,fontWeight:700,color:c,fontFamily:"monospace",lineHeight:1}}>{v}</p>
                        <p style={{fontSize:10,color:C.textSub,marginTop:2}}>{l}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Active toggle */}
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:11,color:C.textSub}}>{m.active?"Active":"Inactive"}</span>
                  <Toggle on={!!m.active} onChange={()=>toggleActive(m.id)}/>
                </div>

                {/* Actions */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {isEditing?(
                    <>
                      <button onClick={()=>{
                        const name=document.getElementById("name-"+m.id)?.value;
                        const email=document.getElementById("email-"+m.id)?.value;
                        const role=document.getElementById("role-"+m.id)?.value;
                        updateMember(m.id,{name,email,role,init:name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()});
                      }} style={{background:C.accent,color:"#fff",border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                        Save
                      </button>
                      <button onClick={()=>setEditingMember(null)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                        Cancel
                      </button>
                    </>
                  ):(
                    <>
                      <button onClick={()=>setEditingMember(m)} style={{background:C.accentMuted,color:C.accent,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                        Edit
                      </button>
                      <button onClick={()=>setShowPermissions(showPerms?null:m.id)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                        Permissions
                      </button>
                      {m.role!=="Admin"&&(
                        <button onClick={()=>deleteMember(m.id)} style={{background:C.redMuted,color:C.red,border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,cursor:"pointer"}}>
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Permissions panel */}
              {showPerms&&(
                <div style={{padding:"12px 20px",borderTop:"1px solid "+C.border,background:C.bgHover}}>
                  <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:10}}>
                    Module access  {m.role==="Admin"?"Full access (Admin)":"Configurable"}
                  </p>
                  {m.role==="Admin"?(
                    <p style={{fontSize:12,color:C.green}}>Admin has access to all modules automatically.</p>
                  ):(
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {ALL_PERMISSIONS.map(p=>{
                        const hasAccess=(m.permissions||[]).includes(p.key)||(m.permissions||[]).includes("all");
                        return(
                          <button key={p.key} onClick={()=>{
                            const perms=m.permissions||[];
                            const newPerms=hasAccess?perms.filter(x=>x!==p.key):[...perms,p.key];
                            updatePermissions(m.id,newPerms);
                          }} style={{background:hasAccess?C.greenMuted:C.bgCard,color:hasAccess?C.green:C.textSub,border:"1px solid "+(hasAccess?C.green+"44":C.border),borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                            <span>{p.icon}</span><span>{p.label}</span>
                            {hasAccess&&<span style={{fontSize:10}}></span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sender accounts section */}
      <div style={{marginTop:24,background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:"16px 20px"}}>
        <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:12}}>Sender Email Accounts</p>
        <p style={{fontSize:12,color:C.textSub,marginBottom:12}}>These email addresses are available as sender options when creating sequences and campaigns.</p>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {team.filter(m=>m.active&&m.email).map(m=>{
            const rc=roleColor(m.role);
            return(
              <div key={m.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",background:C.bgHover,borderRadius:8,border:"1px solid "+C.border}}>
                <Avatar init={m.init||(m.name||"?").slice(0,2).toUpperCase()} size={28} color={rc}/>
                <div style={{flex:1}}>
                  <span style={{fontSize:13,color:C.text,fontWeight:500}}>{m.name}</span>
                  <span style={{fontSize:12,color:C.textSub,marginLeft:10}}>{m.email}</span>
                </div>
                <Badge bg={rc+"1A"} tc={rc} sm>{m.role}</Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}